import { NetworkStatus } from '@apollo/client';
import {
  ErrorRetry,
  ListSelectionStyled,
  SubListSelectionStyled,
  useAppUser,
  useAvailableMilestoneTypesQuery,
} from '@bechtel/client-common';
import { Box, Checkbox, Spinner, SpinnerSize, Text } from '@bechtel/gds';
import { MilestoneType } from '@bechtel/types';
import { Trans } from '@lingui/macro';
import { FieldArray, FieldProps } from 'formik';
import { findIndex, get, orderBy, some } from 'lodash';
import React, { Fragment } from 'react';

export const Milestones: React.FC<FieldProps> = ({ form }) => {
  const { user } = useAppUser();
  const { loading, error, data, refetch, networkStatus } = useAvailableMilestoneTypesQuery(user?.project?.id);

  const milestoneList: MilestoneType[] = orderBy(
    get(data, 'projectConfigById.availableMilestoneTypes', []),
    [(item) => item?.commodityTypes?.[0]?.name?.toLowerCase() || '', (item) => item?.name?.toLowerCase() || ''],
    ['asc', 'asc']
  );

  const hasValues = form.values && form.values.milestoneList && form.values.milestoneList.length > 0;
  return (
    <Fragment>
      {(loading || networkStatus === NetworkStatus.refetch) && (
        <Box alignItems={{ sm: 'center' }} justifyContent={{ sm: 'center' }} p={{ sm: '500' }} data-testid="loading">
          <Spinner size={SpinnerSize.LARGE} />
        </Box>
      )}
      {!loading && error && (
        <Box data-testid="retry">
          <ErrorRetry refetch={refetch}>
            <Trans>There was a problem loading Commodity Type List. Please click the button below to try again.</Trans>
          </ErrorRetry>
        </Box>
      )}
      {!loading && !error && (
        <Box data-testid="success" flexDirection={{ sm: 'column' }}>
          <SubListSelectionStyled
            onClick={() => {
              if (form.values.milestoneList.length !== milestoneList.length) {
                form.setFieldValue('milestoneList', milestoneList);
              } else {
                form.setFieldValue('milestoneList', []);
              }
            }}
          >
            <Box alignItems={{ sm: 'center' }}>
              <Box mr={{ sm: '500' }}>
                <Checkbox
                  checked={hasValues && form.values.milestoneList.length === milestoneList.length}
                  readOnly
                  indeterminate={hasValues && form.values.milestoneList.length < milestoneList.length}
                />
              </Box>
              <Text size="400" weight="600" color="bechtel.grey.1000">
                <Trans>Milestone</Trans>
              </Text>
            </Box>
            {hasValues && (
              <Text size="400" color="bechtel.grey.1000">
                {form.values.milestoneList.length} <Trans>Selected</Trans>
              </Text>
            )}
          </SubListSelectionStyled>
          <FieldArray name="milestoneList">
            {(arrayHelpers) => {
              return (
                <>
                  {milestoneList &&
                    milestoneList.map((milestoneType: MilestoneType) => {
                      const displayText = milestoneType.commodityTypes?.[0]?.name
                        ? `${milestoneType.name} (${milestoneType.commodityTypes?.[0]?.name})`
                        : milestoneType.name;
                      return (
                        <ListSelectionStyled
                          key={milestoneType.id}
                          data-testid={`block-card-search-canvas-milestone-types-${milestoneType.id}`}
                          onClick={() => {
                            const selected = findIndex(form.values.milestoneList, (item: MilestoneType) => {
                              return item.id === milestoneType.id;
                            });
                            if (selected !== -1) {
                              arrayHelpers.remove(selected);
                            } else {
                              arrayHelpers.push(milestoneType);
                            }
                          }}
                        >
                          <Box mr={{ sm: '500' }}>
                            <Checkbox
                              checked={some(form.values.milestoneList, (item: MilestoneType) => {
                                return item.id === milestoneType.id;
                              })}
                              readOnly
                            />
                          </Box>
                          <Text size="400" color="bechtel.grey.1000">
                            {displayText}
                          </Text>
                        </ListSelectionStyled>
                      );
                    })}
                </>
              );
            }}
          </FieldArray>
        </Box>
      )}
    </Fragment>
  );
};
